import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useToast } from '../contexts/ToastContext';
import { calculateRatingChanges, formatRatingChange } from '../services/eloService';
import { getGameById, updateGame as updateGameSupabase, getUserById, updateUser as updateUserSupabase } from '../services/database';

const GameTrackingView = ({ gameId, myUserId, opponentUserId, onGameComplete, onGameStateChange }) => {
  const { showToast } = useToast();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isPlayer1, setIsPlayer1] = useState(false);
  const [waitingForOpponentToStart, setWaitingForOpponentToStart] = useState(false);
  const [waitingForOpponentResult, setWaitingForOpponentResult] = useState(false);
  const startPollingIntervalRef = React.useRef(null);
  const resultPollingIntervalRef = React.useRef(null);

  useEffect(() => {
    fetchGameDetails();
    
    // Cleanup polling on unmount
    return () => {
      if (startPollingIntervalRef.current) {
        clearInterval(startPollingIntervalRef.current);
      }
      if (resultPollingIntervalRef.current) {
        clearInterval(resultPollingIntervalRef.current);
      }
    };
  }, [gameId]);

  const fetchGameDetails = async () => {
    try {
      const raw = await getGameById(gameId);
      // Map to camelCase
      const gameData = {
        ...raw,
        player1Id: raw.player1_id,
        player2Id: raw.player2_id,
        player1: raw.player1 ? { ...raw.player1, inPersonRating: raw.player1.in_person_rating } : null,
        player2: raw.player2 ? { ...raw.player2, inPersonRating: raw.player2.in_person_rating } : null,
        gameType: raw.game_type,
        timeFormat: raw.time_format,
        player1ReadyToStart: raw.player1_ready_to_start,
        player2ReadyToStart: raw.player2_ready_to_start,
        startedAt: raw.started_at,
        endedAt: raw.ended_at,
        player1ReportedResult: raw.player1_reported_result,
        player2ReportedResult: raw.player2_reported_result,
        disputeResolved: raw.dispute_resolved,
        player1RatingBefore: raw.player1_rating_before,
        player2RatingBefore: raw.player2_rating_before,
        player1RatingAfter: raw.player1_rating_after,
        player2RatingAfter: raw.player2_rating_after,
      };
      const amPlayer1 = myUserId === gameData.player1Id;
      
      console.log('📊 Game state updated:', {
        myRole: amPlayer1 ? 'Player 1' : 'Player 2',
        player1Reported: gameData.player1ReportedResult,
        player2Reported: gameData.player2ReportedResult,
        finalResult: gameData.result,
        ended: !!gameData.endedAt,
        disputed: gameData.disputeResolved === false,
      });
      
      setGame(gameData);
      setIsPlayer1(amPlayer1);
    } catch (error) {
      console.error('Error fetching game:', error);
      showToast('Failed to load game details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleStartGame = async () => {
    setSubmitting(true);
    try {
      console.log('🎮 Player clicked Start Game...');
      
      // Mark myself as ready
      const myReadyField = isPlayer1 ? 'player1_ready_to_start' : 'player2_ready_to_start';

      await updateGameSupabase(gameId, { [myReadyField]: true });

      console.log(`✅ Marked ${myReadyField} = true`);
      setWaitingForOpponentToStart(true);
      showToast('Ready! Waiting for opponent to start...', 'info');

      // Poll to check if opponent has also marked ready
      startPollingIntervalRef.current = setInterval(async () => {
        console.log('🔄 Checking if opponent is ready...');
        const polledRaw = await getGameById(gameId);

        const opponentReady = isPlayer1 
          ? (polledRaw.player2_ready_to_start || false)
          : (polledRaw.player1_ready_to_start || false);

        // Both ready and game not started yet → start it!
        if (opponentReady && !polledRaw.started_at) {
          console.log('✅ Both ready! Starting game...');
          clearInterval(startPollingIntervalRef.current);
          startPollingIntervalRef.current = null;

          await updateGameSupabase(gameId, { started_at: new Date().toISOString() });

          setWaitingForOpponentToStart(false);
          showToast('Game started! Good luck! 🎯', 'success');
          fetchGameDetails();
          
          if (onGameStateChange) {
            onGameStateChange('started');
          }
        } else if (polledRaw.started_at) {
          console.log('✅ Opponent started the game!');
          clearInterval(startPollingIntervalRef.current);
          startPollingIntervalRef.current = null;
          setWaitingForOpponentToStart(false);
          showToast('Game started! Good luck! 🎯', 'success');
          fetchGameDetails();
          
          if (onGameStateChange) {
            onGameStateChange('started');
          }
        }
      }, 2000);

      // Timeout after 30 seconds
      setTimeout(() => {
        if (startPollingIntervalRef.current) {
          console.log('⏰ Start timeout - opponent did not accept');
          clearInterval(startPollingIntervalRef.current);
          startPollingIntervalRef.current = null;
          setWaitingForOpponentToStart(false);
          showToast('Opponent did not start in time', 'info');
        }
      }, 30000);

    } catch (error) {
      console.error('Error starting game:', error);
      showToast('Failed to start game', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReportResult = async (myResult) => {
    setSubmitting(true);
    try {
      console.log('🎮 Reporting result:', myResult);
      
      // Determine which field to update based on who I am
      const myResultField = isPlayer1 ? 'player1_reported_result' : 'player2_reported_result';

      await updateGameSupabase(gameId, { [myResultField]: myResult });

      console.log('✅ Your result reported:', myResult);
      setWaitingForOpponentResult(true);
      showToast('Result reported. Waiting for opponent to confirm...', 'info');

      // Poll to check if opponent has also reported
      resultPollingIntervalRef.current = setInterval(async () => {
        console.log('🔄 Checking if opponent reported...');
        const polledRaw = await getGameById(gameId);

        const opponentResult = isPlayer1 
          ? polledRaw.player2_reported_result
          : polledRaw.player1_reported_result;

        // Both reported → finalize!
        if (opponentResult) {
          console.log(`✅ Opponent reported: ${opponentResult}`);
          console.log(`🔍 My result: ${myResult}`);
          clearInterval(resultPollingIntervalRef.current);
          resultPollingIntervalRef.current = null;
          setWaitingForOpponentResult(false);

          // Check if results match
          const resultsMatch = checkResultsMatch(myResult, opponentResult);
          
          if (resultsMatch) {
            console.log('✅ Results match! Finalizing game...');
            await finalizeGame(myResult);
            showToast('Game completed! Stats updated. 🎉', 'success');
          } else {
            console.log('❌ Results don\'t match! Marking as dispute.');
            await markDispute();
            showToast('Results don\'t match! This game is in dispute. ⚠️', 'error');
          }

          fetchGameDetails();
        }
      }, 2000);

      // Timeout after 60 seconds
      setTimeout(() => {
        if (resultPollingIntervalRef.current) {
          console.log('⏰ Result timeout - opponent did not report');
          clearInterval(resultPollingIntervalRef.current);
          resultPollingIntervalRef.current = null;
          setWaitingForOpponentResult(false);
          showToast('Opponent did not report result in time', 'info');
        }
      }, 60000);
    } catch (error) {
      console.error('Error reporting result:', error);
      showToast('Failed to report result', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const checkResultsMatch = (result1, result2) => {
    // Simple check: both players must report the exact same result
    // 
    // Examples:
    // - Player 1 clicks "I Won" → reports PLAYER1_WIN
    // - Player 2 clicks "I Lost" → reports PLAYER1_WIN
    // - Both match! ✅
    //
    // - Player 1 clicks "I Won" → reports PLAYER1_WIN
    // - Player 2 clicks "I Won" → reports PLAYER2_WIN
    // - Don't match! ❌ (both claiming victory)
    //
    // - Both click "Draw" → both report DRAW
    // - Match! ✅
    
    return result1 === result2;
  };

  const finalizeGame = async (agreedResult) => {
    try {
      const player1Rating = game.player1.inPersonRating || 1200;
      const player2Rating = game.player2.inPersonRating || 1200;

      let updateInput = {
        id: gameId,
        endedAt: new Date().toISOString(),
        result: agreedResult,
        disputeResolved: true,
      };

      let myRatingChange = 0;
      let myNewRating = isPlayer1 ? player1Rating : player2Rating;

      // Calculate rating changes only for RATED games
      if (game.gameType === 'RATED') {
        console.log('📊 Calculating ELO for rated game...');
        const ratingChanges = calculateRatingChanges(player1Rating, player2Rating, agreedResult);
        
        updateInput = {
          ...updateInput,
          player1RatingBefore: player1Rating,
          player2RatingBefore: player2Rating,
          player1RatingAfter: ratingChanges.player1NewRating,
          player2RatingAfter: ratingChanges.player2NewRating,
        };

        // Calculate my personal rating change
        if (isPlayer1) {
          myRatingChange = ratingChanges.player1NewRating - player1Rating;
          myNewRating = ratingChanges.player1NewRating;
        } else {
          myRatingChange = ratingChanges.player2NewRating - player2Rating;
          myNewRating = ratingChanges.player2NewRating;
        }

        console.log('✅ ELO calculated:', ratingChanges);
      }

      await updateGameSupabase(gameId, {
        ended_at: updateInput.endedAt,
        result: updateInput.result,
        dispute_resolved: updateInput.disputeResolved,
        ...(updateInput.player1RatingBefore !== undefined ? {
          player1_rating_before: updateInput.player1RatingBefore,
          player2_rating_before: updateInput.player2RatingBefore,
          player1_rating_after: updateInput.player1RatingAfter,
          player2_rating_after: updateInput.player2RatingAfter,
        } : {}),
      });

      // Update user stats for both players
      await updateUserStats(agreedResult, game.gameType === 'RATED', player1Rating, player2Rating);

      fetchGameDetails();

      // Determine if I won, lost, or drew
      const didIWin = 
        (agreedResult === 'PLAYER1_WIN' && isPlayer1) ||
        (agreedResult === 'PLAYER2_WIN' && !isPlayer1);
      const didILose = 
        (agreedResult === 'PLAYER1_WIN' && !isPlayer1) ||
        (agreedResult === 'PLAYER2_WIN' && isPlayer1);
      const isDraw = agreedResult === 'DRAW';

      // Call onGameComplete with result data for popup/navigation
      if (onGameComplete) {
        onGameComplete({
          result: didIWin ? 'win' : didILose ? 'loss' : isDraw ? 'draw' : 'cancelled',
          ratingChange: myRatingChange,
          newRating: myNewRating,
          gameType: game.gameType,
        });
      }
    } catch (error) {
      console.error('Error finalizing game:', error);
      showToast('Failed to finalize game', 'error');
    }
  };

  const markDispute = async () => {
    try {
      await updateGameSupabase(gameId, {
        ended_at: new Date().toISOString(),
        result: 'CANCELLED',
        dispute_resolved: false,
      });

      fetchGameDetails();

      // Navigate back with dispute indicator
      if (onGameComplete) {
        onGameComplete({
          result: 'dispute',
          ratingChange: 0,
          newRating: game.player1.inPersonRating || 1200,
          gameType: game.gameType,
        });
      }
    } catch (error) {
      console.error('Error marking dispute:', error);
    }
  };

  const updateUserStats = async (result, isRated, player1Rating, player2Rating) => {
    try {
      console.log('📊 Updating stats for both players...');
      
      // Update player 1 stats
      await updateSinglePlayerStats(
        game.player1Id,
        result === 'PLAYER1_WIN',
        result === 'PLAYER2_WIN',
        result === 'DRAW',
        isRated,
        true, // isPlayer1
        player1Rating,
        player2Rating,
        result
      );

      // Update player 2 stats
      await updateSinglePlayerStats(
        game.player2Id,
        result === 'PLAYER2_WIN',
        result === 'PLAYER1_WIN',
        result === 'DRAW',
        isRated,
        false, // isPlayer1
        player1Rating,
        player2Rating,
        result
      );

      console.log('✅ Stats updated for both players');
    } catch (error) {
      console.error('Error updating user stats:', error);
    }
  };

  const updateSinglePlayerStats = async (userId, won, lost, drew, isRated, isPlayer1, player1Rating, player2Rating, result) => {
    try {
      // Fetch current user stats
      const userData = await getUserById(userId);

      let updates = {
        wins: (userData.wins || 0) + (won ? 1 : 0),
        losses: (userData.losses || 0) + (lost ? 1 : 0),
        draws: (userData.draws || 0) + (drew ? 1 : 0),
        in_person_rating: userData.in_person_rating || 1200,
      };

      // Update rating for rated games
      if (isRated) {
        const ratingChanges = calculateRatingChanges(player1Rating, player2Rating, result);
        updates.in_person_rating = isPlayer1
          ? ratingChanges.player1NewRating
          : ratingChanges.player2NewRating;
      }

      await updateUserSupabase(userId, updates);
    } catch (error) {
      console.error('Error updating single player stats:', error);
    }
  };

  const handleCancelGame = async () => {
    setSubmitting(true);
    try {
      await updateGameSupabase(gameId, {
        ended_at: new Date().toISOString(),
        result: 'CANCELLED',
      });

      showToast('Game cancelled', 'info');
      fetchGameDetails();

      if (onGameComplete) {
        onGameComplete();
      }
    } catch (error) {
      console.error('Error cancelling game:', error);
      showToast('Failed to cancel game', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="small" color="#94EB98" />
      </View>
    );
  }

  if (!game) {
    return null;
  }

  // Game completed
  if (game.endedAt && game.result) {
    const myReportedResult = isPlayer1 ? game.player1ReportedResult : game.player2ReportedResult;
    const opponentReportedResult = isPlayer1 ? game.player2ReportedResult : game.player1ReportedResult;
    const isDispute = game.disputeResolved === false;

    return (
      <View style={[styles.completedContainer, isDispute && styles.disputeContainer]}>
        <Icon 
          name={isDispute ? "warning" : "check-circle"} 
          size={20} 
          color={isDispute ? "#FF9800" : "#94EB98"} 
        />
        <View style={{ flex: 1 }}>
          <Text style={styles.completedText}>
            {isDispute ? 'Game in dispute' : 'Game completed'}
          </Text>
          {isDispute && (
            <Text style={styles.disputeText}>
              Results don't match. You: {myReportedResult}, Opponent: {opponentReportedResult}
            </Text>
          )}
        </View>
      </View>
    );
  }

  // Game not started
  if (!game.startedAt) {
    const opponentName = isPlayer1 ? game.player2?.username : game.player1?.username;

    // Waiting for opponent to click
    if (waitingForOpponentToStart) {
      return (
        <View style={styles.container}>
          <View style={styles.waitingHeader}>
            <Icon name="hourglass-empty" size={18} color="#94EB98" />
            <Text style={styles.waitingText}>
              Ready! Waiting for {opponentName} to start...
            </Text>
          </View>
        </View>
      );
    }

    // Ready to start
    return (
      <View style={styles.container}>
        <TouchableOpacity
          style={styles.startButton}
          onPress={handleStartGame}
          disabled={submitting}
          activeOpacity={0.8}
        >
          {submitting ? (
            <ActivityIndicator size="small" color="white" />
          ) : (
            <>
              <Icon name="play-circle-filled" size={20} color="white" />
              <Text style={styles.startButtonText}>Start Game</Text>
            </>
          )}
        </TouchableOpacity>
        <Text style={styles.startHint}>Both players must tap to start</Text>
      </View>
    );
  }

  // Waiting for opponent to report result
  if (waitingForOpponentResult) {
    const myReportedResult = isPlayer1 ? game.player1ReportedResult : game.player2ReportedResult;
    return (
      <View style={styles.container}>
        <View style={styles.waitingHeader}>
          <Icon name="hourglass-empty" size={18} color="#94EB98" />
          <Text style={styles.waitingText}>Waiting for opponent to confirm result...</Text>
        </View>
        <Text style={styles.reportedResultText}>
          You reported: {formatResultForDisplay(myReportedResult, isPlayer1)}
        </Text>
      </View>
    );
  }

  // Game in progress - show result buttons
  return (
    <View style={styles.container}>
      <View style={styles.inProgressHeader}>
        <Icon name="timer" size={18} color="#94EB98" />
        <Text style={styles.inProgressText}>Game in progress...</Text>
      </View>

      <Text style={styles.resultLabel}>Report result:</Text>
      
      <View style={styles.resultButtons}>
        <TouchableOpacity
          style={styles.resultButton}
          onPress={() => handleReportResult(isPlayer1 ? 'PLAYER1_WIN' : 'PLAYER2_WIN')}
          disabled={submitting}
          activeOpacity={0.7}
        >
          <Icon name="emoji-events" size={20} color="#2E7D32" />
          <Text style={styles.resultButtonText}>I Won</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.resultButton}
          onPress={() => handleReportResult('DRAW')}
          disabled={submitting}
          activeOpacity={0.7}
        >
          <Icon name="people" size={20} color="#666" />
          <Text style={styles.resultButtonText}>Draw</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.resultButton}
          onPress={() => handleReportResult(isPlayer1 ? 'PLAYER2_WIN' : 'PLAYER1_WIN')}
          disabled={submitting}
          activeOpacity={0.7}
        >
          <Icon name="close" size={20} color="#d32f2f" />
          <Text style={styles.resultButtonText}>I Lost</Text>
        </TouchableOpacity>
      </View>

      <TouchableOpacity
        style={styles.cancelButton}
        onPress={handleCancelGame}
        disabled={submitting}
      >
        <Text style={styles.cancelText}>Cancel Game</Text>
      </TouchableOpacity>
    </View>
  );
};

const formatResultForDisplay = (result, fromPlayer1Perspective) => {
  if (result === 'DRAW') return 'Draw';
  if (result === 'CANCELLED') return 'Cancelled';
  
  if (fromPlayer1Perspective) {
    return result === 'PLAYER1_WIN' ? 'I Won' : 'I Lost';
  } else {
    return result === 'PLAYER2_WIN' ? 'I Won' : 'I Lost';
  }
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#f9f9f9',
    padding: 16,
    borderRadius: 12,
    marginVertical: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  startButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#2E7D32',
    padding: 14,
    borderRadius: 10,
  },
  startButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
  },
  startHint: {
    marginTop: 8,
    fontSize: 12,
    color: '#666',
    textAlign: 'center',
  },
  inProgressHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  inProgressText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#94EB98',
  },
  opponentReportedBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#E3F2FD',
    padding: 10,
    borderRadius: 8,
    marginBottom: 12,
    borderLeftWidth: 3,
    borderLeftColor: '#2196F3',
  },
  opponentReportedText: {
    flex: 1,
    fontSize: 13,
    color: '#1976D2',
    fontWeight: '500',
  },
  waitingHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  waitingText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#94EB98',
  },
  reportedResultText: {
    fontSize: 13,
    color: '#666',
    fontStyle: 'italic',
  },
  resultLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
    marginBottom: 10,
  },
  resultButtons: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 10,
  },
  resultButton: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'white',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  resultButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#333',
  },
  cancelButton: {
    alignItems: 'center',
    padding: 8,
  },
  cancelText: {
    fontSize: 13,
    color: '#999',
    textDecorationLine: 'underline',
  },
  completedContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#e8f5e9',
    padding: 12,
    borderRadius: 10,
    marginVertical: 8,
  },
  disputeContainer: {
    backgroundColor: '#fff3e0',
  },
  completedText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2E7D32',
  },
  disputeText: {
    fontSize: 12,
    color: '#F57C00',
    marginTop: 4,
  },
});

export default GameTrackingView;
